import { useRef, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { queryAPI } from '../services/api';
import { playTextToSpeechRealtime } from '../services/tts';

const STATUS_MESSAGES = {
  retrieval: "Analyzing documents...",
  rerank: "Organizing source data...",
  generation: "Thinking through an answer...",
};

export const useChat = () => {
  const {
    token, 
    messages,
    setMessages,
    setLoading,
    setError,
    updateConversationTitle,
    activeConversationId,
  } = useAppContext();

  const [copySuccess, setCopySuccess] = useState(false);
  const [showSourcesForMessage, setShowSourcesForMessage] = useState(null);
  const [playingMessageId, setPlayingMessageId] = useState(null);
  const activeRequestRef = useRef(null);

  const sanitizeHistory = (history = []) =>
    history.filter((message) => {
      if (!message || typeof message.content !== 'string') return false;
      return message.content.trim().length > 0;
    });

  const handleReadAloud = async (text, messageIndex) => {
    if (playingMessageId !== null) return;

    try {
      setPlayingMessageId(messageIndex);
      
      // Strip markdown formatting
      const plainText = text
        .replace(/#{1,6}\s/g, '')           // Remove headers
        .replace(/\*\*(.+?)\*\*/g, '$1')    // Remove bold
        .replace(/\*(.+?)\*/g, '$1')        // Remove italic
        .replace(/_(.+?)_/g, '$1')          // Remove italic underscore
        .replace(/`{1,3}[^`\n]+`{1,3}/g, '') // Remove code blocks
        .replace(/\[(.+?)\]\(.+?\)/g, '$1') // Remove links, keep text
        .replace(/^\s*[-*+]\s/gm, '')       // Remove bullet points
        .replace(/^\s*\d+\.\s/gm, '')       // Remove numbered lists
        .replace(/>\s/g, '')                // Remove blockquotes
        .replace(/\n{2,}/g, '\n')           // Remove extra newlines
        .trim();
      
      await playTextToSpeechRealtime(plainText, 'fr');
      setPlayingMessageId(null);
    } catch (error) {
      console.error("Failed to play audio:", error);
      setError("Could not read the answer aloud.");
      setPlayingMessageId(null);
    }
  };

  const handleQuerySubmit = async (query) => {
    if (!query.trim()) return;
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
      activeRequestRef.current = null;
    }

    let cleanQuery = query.trim();
    if (cleanQuery.endsWith("?")) {
      cleanQuery = cleanQuery.slice(0, -1);
    }
    const conversationId = activeConversationId;
    const sanitizedHistory = sanitizeHistory(messages);
    const userMessage = { role: 'user', content: cleanQuery };
    const botPlaceholder = { role: 'bot', content: '', sources: [], status: STATUS_MESSAGES.retrieval };
    setMessages(prev => [...sanitizeHistory(prev), userMessage, botPlaceholder], conversationId);
    setError(null);
    setCopySuccess(false);
    setLoading(true);

    let titleUpdated = false;
    let latestAnswerText = "";
    const queryStartTime = Date.now(); // Capture start time for response timing
    const controller = new AbortController();
    activeRequestRef.current = controller;

    const deriveFallbackTitle = () => {
      if (latestAnswerText) {
        const firstSentence = latestAnswerText.split(/[.!?]/)[0] || latestAnswerText;
        return firstSentence.trim().substring(0, 80) || "New Chat";
      }
      return cleanQuery.substring(0, 80) || "New Chat";
    };

    try {
      await queryAPI(cleanQuery, sanitizedHistory, token, conversationId, (eventType, data) => {
        if (eventType === 'title' && data?.title) {
          updateConversationTitle(data.title, { force: true, conversationId });
          titleUpdated = true;
          return;
        }

        setMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          if (!lastMessage || lastMessage.role !== 'bot') return prev;
          let updatedLastMessage = { ...lastMessage };
          if (eventType === 'text_chunk' && data.content) {
            latestAnswerText += data.content;
            if (updatedLastMessage.status) {
              updatedLastMessage.status = null;
            }
            updatedLastMessage.content += data.content;
          } else if (eventType === 'sources') {
            updatedLastMessage.sources = data.sources || [];
          } else if (eventType === 'status') {
            const statusText = data?.message || (data?.stage ? STATUS_MESSAGES[data.stage] : null);
            updatedLastMessage.status = statusText || null;
          } else if (eventType === 'error') {
            setError(data.message);
            updatedLastMessage.status = null;
          } else if (eventType === 'end_stream') {
            const totalTime = Date.now() - queryStartTime;
            updatedLastMessage.responseTime = totalTime;
            updatedLastMessage.status = null;
            if (!titleUpdated) {
              const fallbackTitle = deriveFallbackTitle();
              updateConversationTitle(fallbackTitle, { force: true, conversationId });
              titleUpdated = true;
            }
          }
          return [...prev.slice(0, -1), updatedLastMessage];
        }, conversationId);
      }, { signal: controller.signal });
    } catch (error) {
      if (error?.name === 'AbortError') {
        setMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          if (!lastMessage || lastMessage.role !== 'bot') return prev;
          const hasContent = Boolean(lastMessage.content?.trim());
          const hasSources = Array.isArray(lastMessage.sources) && lastMessage.sources.length > 0;
          if (!hasContent && !hasSources) {
            return prev.slice(0, -1);
          }
          const updatedLastMessage = { ...lastMessage, status: null };
          return [...prev.slice(0, -1), updatedLastMessage];
        }, conversationId);
        if (!titleUpdated) {
          const fallbackTitle = deriveFallbackTitle();
          updateConversationTitle(fallbackTitle, { force: true, conversationId });
          titleUpdated = true;
        }
        return;
      }
      setError('Failed to fetch from the server. Is the backend running?');
      console.error("Fetch error:", error);
      setMessages(prev => prev.filter(m => m !== userMessage && m !== botPlaceholder), conversationId);
    } finally {
      setLoading(false);
      if (activeRequestRef.current === controller) {
        activeRequestRef.current = null;
      }
    }
  };

  const stopGeneration = () => {
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
    }
  };

  const copyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
    document.body.removeChild(textArea);
  };

  return {
    handleQuerySubmit,
    copyToClipboard,
    copySuccess,
    showSourcesForMessage,
    setShowSourcesForMessage,
    playingMessageId,
    handleReadAloud,
    stopGeneration,
  };
};
